import { AppContext } from 'contexts/AppProvider'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'store'
import { useNavigate } from 'react-router-dom'
import {
  OrderStatusEnum,
  type PaymentMethodQuery,
  PaymentStatusEnum,
  PaymentTypeEnum,
  useUpdateOrderPaymentMethodMutation,
} from 'graphQL/dinein/generate/operations'
import { CustomerPaymentTypeEnum } from 'graphQL/hatostore/generate/operations'
import {
  buffetSelector,
  resetBuffetModal,
  setBuffetModal,
  setPaymentMethod as setBuffetPaymentMethod,
} from 'store/slices/buffetSlice'
import { PaymentMethod } from 'types/payment'
import { dineInClient } from 'variables/services'
import { ModalEnum } from 'components/buffet/BuffetModals'
import { sendAnalyticsDineInAddPaymentInfo } from 'helpers/analytics'
import { SubPageName } from 'enums/buffet'
import { filterBuffetPaymentMethods } from 'helpers/payment'

export interface CurrentOrder {
  id: number
  omiseCardID: string
  omiseCardBrand: string
  omiseCardLastDigits: string
  omiseCardholderName: string
  paymentType: PaymentTypeEnum
  paymentPhone: string
}

interface CurrentUser {
  omiseCardLastDigits: string
  omiseCardholderName: string
  omiseCardID: string
  omiseCardBrand: string
  defaultPaymentMethod: CustomerPaymentTypeEnum
}

type Props = {
  currentOrder: CurrentOrder
  paymentMethods: PaymentTypeEnum[]
  refetchData: () => Promise<PaymentMethodQuery>
}

const initPaymentMethod: PaymentMethod = {
  type: PaymentTypeEnum.None,
  phoneNumber: '',
  rememberCard: false,
  creditCard: {
    id: '',
    brand: '',
    lastDigits: '',
    holderName: '',
  },
}

const usePaymentMethod = ({ currentOrder, paymentMethods = [], refetchData }: Props) => {
  const { currentUser } = useContext(AppContext)
  const buffet = useSelector(buffetSelector)
  const dispatch = useAppDispatch()
  const [statePaymentMethodCreditCard, setStatePaymentMethodCreditCard] = useState<PaymentMethod | null>(null)
  const [isUpdatePayment, setIsUpdatePayment] = useState(false)
  const navigate = useNavigate()

  const buffetPaymentMethods = useMemo(() => filterBuffetPaymentMethods(paymentMethods), [paymentMethods])

  const [updateOrderPaymentMethod] = useUpdateOrderPaymentMethodMutation({ client: dineInClient })

  useEffect(() => refreshPaymentMethod(), [currentUser?.omiseCardLastDigits, buffetPaymentMethods])

  const setPaymentMethod = (_paymentMethod: PaymentMethod) => dispatch(setBuffetPaymentMethod(_paymentMethod))

  function refreshPaymentMethod(parameters?: {
    currentOrderByPass?: CurrentOrder
    currentUserByPass?: CurrentUser
    isNotUseStatePaymentMethodCreditCard: boolean
  }) {
    const _currentOrder = parameters?.currentOrderByPass ? parameters.currentOrderByPass : currentOrder
    const _currentUser = parameters?.currentUserByPass ? parameters.currentUserByPass : currentUser
    const _statePaymentMethodCreditCard = parameters?.isNotUseStatePaymentMethodCreditCard
      ? null
      : statePaymentMethodCreditCard
    let omiseCreditCard = initPaymentMethod
    if (_currentOrder?.omiseCardID) {
      omiseCreditCard = {
        type: PaymentTypeEnum.OmiseCreditCard,
        phoneNumber: '',
        creditCard: {
          id: _currentOrder.omiseCardID,
          brand: _currentOrder.omiseCardBrand,
          lastDigits: _currentOrder.omiseCardLastDigits,
          holderName: _currentOrder.omiseCardholderName,
        },
        rememberCard: true,
      }
    } else if (_currentUser?.omiseCardLastDigits) {
      omiseCreditCard = {
        type: PaymentTypeEnum.OmiseCreditCard,
        phoneNumber: '',
        creditCard: {
          id: _currentUser.omiseCardID,
          brand: _currentUser.omiseCardBrand,
          lastDigits: _currentUser.omiseCardLastDigits,
          holderName: _currentUser.omiseCardholderName,
        },
        rememberCard: true,
      }
    }
    if (buffetPaymentMethods) {
      const defaultPaymentMethod = _currentOrder.paymentType ?? _currentUser.defaultPaymentMethod
      const phoneNumber = _currentOrder.paymentPhone ? _currentOrder.paymentPhone : _currentUser.defaultPaymentPhone

      if (_statePaymentMethodCreditCard) {
        setPaymentMethod(_statePaymentMethodCreditCard)
      } else if (_currentOrder?.omiseCardID || _currentUser?.omiseCardLastDigits) {
        // User have credit card.
        if (defaultPaymentMethod !== null) {
          // Have default payment
          if (buffetPaymentMethods.includes(defaultPaymentMethod)) {
            setPaymentMethod({
              ...omiseCreditCard,
              type: defaultPaymentMethod,
              phoneNumber,
            })
          } else {
            setPaymentMethod({
              ...omiseCreditCard,
              type: PaymentTypeEnum.None,
              phoneNumber,
            })
          }
        } else {
          // Not have default payment
          setPaymentMethod({
            ...omiseCreditCard,
            type: PaymentTypeEnum.None,
            phoneNumber,
          })
        }
      } else if (!_currentUser?.omiseCardLastDigits) {
        // User not have credit card.
        if (defaultPaymentMethod !== null) {
          // Have default payment
          if (
            buffetPaymentMethods.includes(defaultPaymentMethod) &&
            defaultPaymentMethod !== PaymentTypeEnum.OmiseCreditCard
          ) {
            setPaymentMethod({
              type: defaultPaymentMethod,
              phoneNumber,
              rememberCard: false,
              creditCard: initPaymentMethod.creditCard,
            })
          } else {
            setPaymentMethod({
              type: PaymentTypeEnum.None,
              phoneNumber,
              rememberCard: false,
              creditCard: initPaymentMethod.creditCard,
            })
          }
        } else {
          // Not have default payment
          setPaymentMethod({
            type: PaymentTypeEnum.None,
            phoneNumber,
            rememberCard: false,
            creditCard: initPaymentMethod.creditCard,
          })
        }
      }
    }
  }

  async function checkCurrentOrder() {
    const result = await refetchData()
    if (result) {
      const { currentOrder } = result
      if (result && currentOrder.userID && currentUser) {
        if (currentUser.id === currentOrder.userID) {
          const isCanCheckout = currentOrder.status !== OrderStatusEnum.Completed
          if (isCanCheckout) {
            dispatch(resetBuffetModal())
            return true
          }
        } else if (currentOrder.payments.length) {
          const { payments } = currentOrder
          const indexLastPyament = payments.length - 1
          const { status } = payments[indexLastPyament]
          if (status === PaymentStatusEnum.PendingPayment) {
            dispatch(setBuffetModal({ type: ModalEnum.PendingPayment, isProcessing: false }))
          } else {
            dispatch(setBuffetModal({ type: ModalEnum.ConfirmOverrideCheckout, isProcessing: false }))
          }
        } else {
          dispatch(setBuffetModal({ type: ModalEnum.ConfirmOverrideCheckout, isProcessing: false }))
        }
        return false
      }
    }
    return false
  }

  async function updatePayment({ orderID, cardID, type, phoneNumber }) {
    setIsUpdatePayment(true)
    const canUpdatePayment = await checkCurrentOrder()
    if (canUpdatePayment) {
      await updateOrderPaymentMethod({
        variables: {
          orderID,
          cardID,
          type,
          phoneNumber: phoneNumber ? phoneNumber : '',
        },
      })
    }
    setIsUpdatePayment(false)
  }

  async function onSelectPayment(paymentMethod) {
    setIsUpdatePayment(true)
    try {
      await updatePayment({
        orderID: buffet.table.orderID,
        cardID: paymentMethod?.creditCard?.id,
        type: paymentMethod.type,
        phoneNumber: paymentMethod?.phoneNumber,
      })

      setPaymentMethod(paymentMethod)
      if (paymentMethod?.creditCard) {
        setStatePaymentMethodCreditCard(paymentMethod)
      } else {
        setStatePaymentMethodCreditCard(null)
      }
      navigate(SubPageName.Checkout)
      sendAnalyticsDineInAddPaymentInfo({ paymentInfo: { type: paymentMethod.type } })
    } catch (error) {
      setIsUpdatePayment(false)
    }
  }

  return {
    refreshPaymentMethod,
    checkCurrentOrder,
    onSelectPayment,
    updatePayment,
    paymentMethod: buffet.paymentMethod,
    setPaymentMethod,
    buffetPaymentMethods,
    setStatePaymentMethodCreditCard,
    isUpdatePayment,
  }
}

export default usePaymentMethod
